import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { ShieldCheck, AlertTriangle } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

/**
 * Quick inline editor for the property's annual taxes + insurance — the two
 * fields that drive the Tax + Insurance Coverage banner on the SREO report.
 * No address re-geocode, no schema change: just PUT the full property doc
 * back with the two updated numbers.
 */
export default function SetTaxInsuranceDialog({ property, open, onOpenChange, onSaved }) {
  const [taxes, setTaxes] = useState(0);
  const [insurance, setInsurance] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!property) return;
    setTaxes(Number(property.annual_taxes) || 0);
    setInsurance(Number(property.annual_insurance) || 0);
  }, [property]);

  if (!property) return null;

  const submit = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      // PUT requires a full PropertyCreate payload; reuse the existing doc.
      const payload = {
        name: property.name,
        property_type: property.property_type,
        address: property.address,
        units: Number(property.units) || 0,
        purchase_price: Number(property.purchase_price) || 0,
        market_value: Number(property.market_value) || 0,
        purchase_date: property.purchase_date || null,
        sale_date: property.sale_date || null,
        sale_price: property.sale_price ?? null,
        notes: property.notes || "",
        annual_taxes: Number(taxes) || 0,
        annual_insurance: Number(insurance) || 0,
        // Pass through existing optional fields so we don't blank them
        bedrooms: property.bedrooms ?? null,
        bathrooms: property.bathrooms ?? null,
        square_feet: property.square_feet ?? null,
        year_built: property.year_built ?? null,
        zoning: property.zoning || null,
        lat: property.lat ?? null,
        lng: property.lng ?? null,
        formatted_address: property.formatted_address ?? null,
      };
      await api.put(`/properties/${property.id}`, payload);
      toast.success("Tax & insurance updated", {
        description: `${property.name}: $${(Number(taxes) || 0).toLocaleString()} taxes · $${(Number(insurance) || 0).toLocaleString()} insurance / yr`,
      });
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error("Update failed", { description: err.response?.data?.detail || err.message });
    } finally {
      setBusy(false);
    }
  };

  const isMissing = (Number(property.annual_taxes) || 0) === 0
    || (Number(property.annual_insurance) || 0) === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }}>
            Set tax & insurance
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4" data-testid="set-ti-form">
          <div className="text-xs text-slate-600 leading-relaxed">
            Annual obligations for <strong>{property.name}</strong>. These power the
            <strong> Tax + Insurance Coverage</strong> banner on the SREO report.
          </div>

          {isMissing ? (
            <div className="border border-amber-200 bg-amber-50 text-amber-900 text-xs p-2.5 rounded-sm flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>This property is currently <strong>not configured</strong>. It won't appear in the coverage check until both fields are set.</span>
            </div>
          ) : (
            <div className="border border-emerald-200 bg-emerald-50 text-emerald-900 text-xs p-2.5 rounded-sm flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" />
              <span>Both fields are configured. Updating below will recalculate coverage immediately.</span>
            </div>
          )}

          <div>
            <label className="overline text-slate-600">Annual taxes</label>
            <div className="flex items-center mt-1">
              <span className="px-3 py-2 bg-slate-100 border border-r-0 border-slate-200 rounded-l-sm font-mono text-slate-500 text-sm">$</span>
              <input
                type="number"
                value={taxes}
                onChange={(e) => setTaxes(e.target.value)}
                min={0}
                step={100}
                className="input-clean rounded-l-none flex-1 font-mono"
                data-testid="set-ti-taxes-input"
                autoFocus
              />
            </div>
          </div>

          <div>
            <label className="overline text-slate-600">Annual insurance</label>
            <div className="flex items-center mt-1">
              <span className="px-3 py-2 bg-slate-100 border border-r-0 border-slate-200 rounded-l-sm font-mono text-slate-500 text-sm">$</span>
              <input
                type="number"
                value={insurance}
                onChange={(e) => setInsurance(e.target.value)}
                min={0}
                step={100}
                className="input-clean rounded-l-none flex-1 font-mono"
                data-testid="set-ti-insurance-input"
              />
            </div>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 flex items-baseline justify-between">
            <span className="overline text-slate-600">Total / year</span>
            <span className="font-mono font-bold text-slate-900 text-sm">
              ${((Number(taxes) || 0) + (Number(insurance) || 0)).toLocaleString()}
            </span>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="btn-outline-dark text-sm"
              data-testid="set-ti-cancel-btn"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              className="btn-primary-dark text-sm disabled:opacity-60"
              data-testid="set-ti-save-btn"
            >
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
